import { IContext, IContextSelector, IPageQuery } from "../interfaces";
import { useState, KeyboardEvent, useRef, useEffect, useMemo } from "react";
import { InfiniteScroller } from "../components";
import { useClickOutside } from "../hooks/ClickOutsideHook";
import { usePageFetch } from "../hooks/PageHook";
import navigationService from "../services/navigationService";
import { handleKeyDown } from "../utils";
import { useDebounce } from "../hooks/DebounceHook";
import { useListNavigation } from "../hooks/ListNavigation";
import { SearchInput } from "../components/SearchInput";
import { t } from "i18next";
import { useCallback } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import React from "react";

export const NavBarContextSelector = (props: IContextSelector) => {
    const [ref, showList, setShowList] = useClickOutside<HTMLDivElement>();
    const [pageQuery, setPageQuery] = useState<IPageQuery>({ PageNumber: 1, PageSize: 10, Filters: { name: "" } });
    const debouncedSearch = useDebounce<IPageQuery>(pageQuery, 500);
    const [options, totalCount, hasMore, loadNextPage] = usePageFetch<IContext>(navigationService.getPage, debouncedSearch);
    const { activeItem, setActiveItem, clearState, focusNext, focusPrevious, focusFirst, focusLast } = useListNavigation<IContext>(options);
    const companiesListRef = useRef<HTMLUListElement>(null);
    const inputSearchRef = useRef<HTMLInputElement>(null);
    const optionsRefs = useRef<(HTMLButtonElement | null)[]>([]);

    useEffect(() => {
        if (activeItem == null)
            return;

        if (activeItem.Id === options[options.length - 1].Id && hasMore)
            loadNextPage();

        var list = companiesListRef.current;
        if (list != null && list.getElementsByClassName("active")[0] != null)
            list.getElementsByClassName("active")[0].scrollIntoView(false);
    }, [activeItem]);

     const handleKeyDownGaiaButton = useCallback((event: KeyboardEvent<HTMLButtonElement>, idx: number) => {
        event.stopPropagation();
        if (event.key === "ArrowDown") {
            event.preventDefault();
            focusNext();
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            focusPrevious();
        }
       
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (activeItem) {
                handleClickContext(activeItem);
                setShowList(false);
            }
        }
        if (event.key === "Escape" || event.key === "Tab") {
            setShowList(false);
        }
    }, [activeItem, focusNext, focusPrevious, focusFirst, focusLast, handleClickContext, setShowList]);


      useEffect(() => {
        if (showList && activeItem) {
            const idx = options.findIndex(o => o.Id === activeItem.Id);
            if (idx >= 0 && optionsRefs.current[idx]) {
                optionsRefs.current[idx]?.focus();
            }
        }
    }, [activeItem, showList, options]);

    function handleChangeSearch(value: string) {
        setPageQuery({ PageNumber: 1, PageSize: 10, Filters: { name: value } });
    }

    function handleClickContext(newContext: IContext) {
        setPageQuery({ PageNumber: 1, PageSize: 10, Filters: { name: "" } });
        props.onChange(newContext);
    }

    function handleKeyDownButton(event: KeyboardEvent<HTMLButtonElement>) {
        handleKeyDown(event, "Enter", () => { event.stopPropagation(); setShowList(true) });
        handleKeyDown(event, "Escape", () => { setShowList(false) });
    };

    const handleTabKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if(showList === true)
            handleKeyDown(event, 'Tab', () => setShowList(false));
    }
    
    const handleTabKeyDownMemo  = useMemo(() => handleTabKeyDown, [showList]);
    
    function handleKeyDownSearch(event: KeyboardEvent<HTMLInputElement>) {
        event.stopPropagation();
        handleKeyDown(event, "Enter", () => {
            activeItem && handleClickContext(activeItem);
            setShowList(false);
        });
        handleKeyDown(event, 'ArrowDown', () => focusNext());
        handleKeyDown(event, 'ArrowUp', () => focusPrevious());
        handleKeyDown(event, 'Escape', () => setShowList(false));
        handleTabKeyDownMemo(event);
    }

    function handleClickButton(event: React.MouseEvent<HTMLButtonElement>) {
        clearState();
        setShowList(!showList);
        setTimeout(() => {
            inputSearchRef.current?.focus();
        }, 0);
    }
    
    return (
        <>
            <div ref={ref}
                className={`w-100 dropdown ${showList === true ? "open" : ""} d-block ar-context-selector mt-8`}
                data-testid="context-selector-dropdown">
                <button type="button"
                    onClick={handleClickButton}
                    className={window.useGaiaDesign ? `ga-input w-100` :`btn dropdown-toggle`}
                    data-toggle="dropdown"
                    id="context-selector-dropdown"
                    data-testid="context-selector-toggle"
                    aria-haspopup="true"
                    onKeyDown={handleKeyDownButton}
                    aria-expanded={showList}>
                    <span className="context-name">
                        {props.currentContext.Name}
                         {window.useGaiaDesign && 
                            <>
                                {!showList && <ChevronDownIcon className="ga-select__arrow" size={16} />}
                                {showList && <ChevronUpIcon className="ga-select__arrow" size={16} />}
                            </>
                        }
                    </span>
                    {!window.useGaiaDesign &&  <span className="caret"></span>}
                </button>

                <div className="dropdown-menu w-100">
                    <SearchInput
                        value={pageQuery.Filters?.name}
                        placeholder={`${t("Search", { ns: "general" })}...`}
                        ariaLabel={`${t("Search", { ns: "general" })}...`}
                        onChange={handleChangeSearch}
                        onKeyDown={handleKeyDownSearch}
                        inputRef={inputSearchRef as React.RefObject<HTMLInputElement>}
                        autoComplete="new-search"
                        dataTestId="search-context-input"
                        tabIndex={0} />
                    <ul role="menu" className="ar-custom-list p-0" aria-hidden={!showList} ref={companiesListRef}>
                        {options.map((opt: IContext, idx) =>
                            <React.Fragment key={opt.Id}>
                            {!window.useGaiaDesign &&
                                <li className={(activeItem == null && opt.Id === props.currentContext.Id) || activeItem?.Id === opt.Id ? "active" : ''}
                                    role="menuitem"
                                    data-testid={`context-list-element-${opt.Id}`}>
                                        <a onClick={() => handleClickContext(opt)}>
                                            {opt.Name}
                                        </a>
                                </li>
                            }
                            {window.useGaiaDesign &&
                                <button
                                    ref={el => { optionsRefs.current[idx] = el; }}
                                    className={`ga-dropdown__item w-100 ${opt.Id === props.currentContext.Id ? "ga-dropdown__item--selected" : ""}`}
                                    onClick={() => handleClickContext(opt)}
                                    type="button"
                                    tabIndex={activeItem?.Id === opt.Id ? 0 : -1}
                                    aria-selected={opt.Id === props.currentContext.Id}
                                    onKeyDown={e => handleKeyDownGaiaButton(e, idx)}>
                                        <div className="ga-dropdown__item-label">{opt.Name}</div>
                                </button>
                            }
                            </React.Fragment>
                        )}
                        <li>
                            <InfiniteScroller data={options} onLoadMore={loadNextPage} disabled={!hasMore} />
                        </li>
                    </ul>
                </div>
            </div>
        </>);
}